<script>
import { v4 as uuidv4 } from 'uuid';
import draggable from 'vuedraggable';
import ChannelSelectGroup from '@/components/watchmen/channels/ChannelSelectGroup';
import BasicModal from "@/components/ui/BasicModal";
import Button2 from '@/components/ui/Button2';
import ShiftForm from './ShiftForm';
import ShiftCardCompact from './ShiftCardCompact';
import UiCard from '@/components/ui/Card';
import TimezoneEditor from '@/components/watches/TimezoneEditor'
import { SHIFT_DEFAULT } from '@/components/watches/SHIFT_DEFAULT.js';
import {useDvFlagAccess} from "../../services/featureFlag";

export default {
  name: "WatchesForm",
  components: {
    BasicModal,
    Button2,
    ChannelSelectGroup,
    ShiftForm,
    ShiftCardCompact,
    draggable,
    UiCard,
    TimezoneEditor
  },
  props: {
    value: {
      type: Object,
    },
    datisLicences: {
      type: Object,
    },
  },
  data() {
    return {
      SHIFT_DEFAULT,
      watches: this.value,
      shiftIndexInEditModal: null,
      shiftInEditModal: null,
    };
  },
  methods: {
    useDvFlagAccess,
    cloneShift(shift) {
      return JSON.parse(JSON.stringify(shift));
    },
    handleAddShift: function () {
      this.shiftInEditModal = {
        ...this.SHIFT_DEFAULT,
        channels: {...this.SHIFT_DEFAULT.channels},
        name: `Shift ${this.shiftCount + 1}`,
        id: uuidv4()
      }
      this.$refs["createShiftModal"].showModal();
    },
    openEditShiftModal: function (index) {
      this.shiftIndexInEditModal = index;
      this.shiftInEditModal = this.cloneShift(this.watches.shifts[index]);
      this.$refs["editShiftModal"].showModal();
    },
    handleShiftActiveChange: function ({ id, active }) {
      this.watches.shifts = this.watches.shifts.map(shift => {
        if (shift.id === id) {
          return { ...shift, active }
        }
        return shift
      })
      this.$emit("input", this.watches)
    },
    handleUpdateShift: function (shift) {
      this.watches.shifts = this.watches.shifts.map(item => {
        if (item.id === shift.id) {
          return this.cloneShift(shift)
        }
        return item
      })
      this.$emit("input", this.watches)
      this.$refs["editShiftModal"].hideModal();
    },
    handleCreateShift: function () {
      this.watches.shifts = [
        this.cloneShift(this.shiftInEditModal),
        ...this.watches.shifts,
      ]
      this.$emit("input", this.watches)
      this.$refs["createShiftModal"].hideModal();
    },
    handleDeleteShift: function (index) {
      this.watches.shifts.splice(index, 1);
      this.$emit("input", this.watches)
      this.$refs["editShiftModal"].hideModal();
    },
  },
  computed: {
    shiftCount() {
      return this.watches?.shifts?.length || 0;
    },
    isActiveShiftDisabled() {
      if(!useDvFlagAccess("beepiz-shift-can:read"))
        return useDvFlagAccess("beepiz-shift-can:read")

      return useDvFlagAccess("beepiz-shift-can:read")
    },
  },
  watch: {
    value: {
      handler(newVal) {
        this.watches = {
          ...newVal,
          shifts: JSON.parse(JSON.stringify(newVal.shifts))
        }
      },
      deep: true
    },
  },
}
</script>

<template>
  <div>
    <div class="row">
      <div class="col-lg-6">
        <div>
          <h3 class="mb-4">
            {{ $t('watches.title_default') }}
          </h3>
          <ChannelSelectGroup v-model="watches.default" class="mb-5" />
        </div>
      </div>
      <div class="col-lg-6">
        <div>
          <div class="d-flex align-items-center mb-4">
            <h3 class="m-0">{{ $t('watches.title_shifts') }}</h3>
            <BFormCheckbox
              v-model="value.shifts_active"
              name="active-watches"
              switch
              :aria-label="$t('watches.active_watch')"
              size="lg"
              style="margin-top: -0.3125rem; margin-right: -.5em;"
              :disabled="!isActiveShiftDisabled"
              class="ml-auto"
            />
          </div>
          <div>
            <div
              v-if="!isActiveShiftDisabled"
              class="alert alert-warning mb-4"
              v-text="$t('watches.no_pro_licences')"
            ></div>

          </div>
          <section :style="{ opacity: watches.shifts_active ? 1 : .5 }" class="mt-4">
            <div class="mb-4">
              <TimezoneEditor v-model="value.timezone" />
            </div>
            <div class="mb-4">
              <Button2 @click.native.prevent="handleAddShift" :disabled="!watches.shifts_active">
                <i class="fa fa-plus" aria-hidden="true"></i>
                {{ $t('watches.add_shift') }}
              </Button2>
            </div>
            <div v-if="!shiftCount" class="mb-4 text-center">
              <div class="alert alert-info" v-html="$t('watches.empty_shifts')"></div>
            </div>
            <draggable
              :list="watches.shifts"
              @start="dragging = true"
              @end="dragging = false"
              handle=".handle"
              ghost-class="ghost"
            >
              <ShiftCardCompact
                v-for="(shift, index) in watches.shifts"
                :key="shift.id"
                :value="shift"
                @active-change="handleShiftActiveChange"
                @edit-shift="openEditShiftModal(index)"
                class="mb-1"
              />
            </draggable>
          </section>
        </div>
      </div>
    </div>

    <BasicModal ref="createShiftModal" :title="$t('watches.addShift')" size="xl">
      <template v-slot:main>
        <ShiftForm
          v-model="shiftInEditModal"
          :proLicences="isActiveShiftDisabled"
          @edit-shift="handleUpdateShift(shiftInEditModal)"
        />
      </template>
      <template v-slot:footer>
        <div class="d-flex">
          <Button2
            @click.native.prevent="handleCreateShift(shiftInEditModal)"
            class="btn-brand ml-auto"
          >{{ $t('global.add')}}</Button2>
        </div>
      </template>
    </BasicModal>
    <BasicModal ref="editShiftModal" :title="$t('watches.editShift')" size="xl">
      <template v-slot:main>
        <ShiftForm
          v-model="shiftInEditModal"
          :proLicences="useDvFlagAccess('beepiz-shift-can:read')"
          @edit-shift="handleUpdateShift(shiftInEditModal)"
        />
      </template>
      <template v-slot:footer>
        <div class="d-flex flex-row-reverse" style="gap: 1rem">
          <Button2
            @click.native.prevent="handleUpdateShift(shiftInEditModal)"
            class="btn-brand"
          >{{ $t('global.validate')}}</Button2>
          <Button2
            @click.native.prevent="handleDeleteShift(shiftIndexInEditModal)"
            class="btn-danger"
          >{{ $t('global.delete')}}</Button2>
        </div>
      </template>
    </BasicModal>

  </div>
</template>